/**
 * Nota fiscal de serviço (0176, 0177) — o contrato da tela.
 *
 * Duas pontas com a mesma forma: a clínica emite para o paciente (alvo
 * `clinica`), a plataforma emite para a clínica (alvo `plataforma`). O emitente
 * é a configuração fiscal de quem emite; a nota nasce rascunho, com tomador e
 * serviço congelados, e só vai à emissão sem pendência e com a empresa ligada ao
 * emissor (Focus NFe). O município decide o padrão: o nacional ou o da prefeitura.
 */

export type AlvoFiscal = 'clinica' | 'plataforma';

export type RegimeTributario = 'simples_nacional' | 'mei' | 'lucro_presumido' | 'lucro_real';

export const REGIMES: { valor: RegimeTributario; rotulo: string }[] = [
    { valor: 'simples_nacional', rotulo: 'Simples Nacional' },
    { valor: 'mei', rotulo: 'MEI' },
    { valor: 'lucro_presumido', rotulo: 'Lucro presumido' },
    { valor: 'lucro_real', rotulo: 'Lucro real' },
];

/** Como o município emite: pelo sistema nacional (DPS) ou pelo webservice da prefeitura. */
export type PadraoNfse = 'nacional' | 'municipal';

/** Regime especial de tributação, na tabela do padrão nacional. */
export const REGIMES_ESPECIAIS: { valor: number; rotulo: string }[] = [
    { valor: 0, rotulo: 'Nenhum' },
    { valor: 3, rotulo: 'Microempresa municipal' },
    { valor: 2, rotulo: 'Estimativa' },
    { valor: 6, rotulo: 'Sociedade de profissionais' },
    { valor: 1, rotulo: 'Cooperativa' },
    { valor: 5, rotulo: 'Profissional autônomo' },
    { valor: 4, rotulo: 'Notário ou registrador' },
    { valor: 9, rotulo: 'Outros' },
];

/** A ligação com o emissor. */
export interface EmissorFiscal {
    padrao: PadraoNfse | null;
    ligadoEm: string | null;
    /** A última recusa ao ligar, na frase do emissor. */
    mensagem: string | null;
    /** A prefeitura cancela pelo sistema? Nulo: o emissor não sabe. */
    cancelaPorApi: boolean | null;
    /** O cadastro mudou depois de ligar: falta "Atualizar no emissor". */
    desatualizado: boolean;
}

export interface CertificadoFiscal {
    /** Na ICP-Brasil, "RAZÃO SOCIAL:CNPJ". */
    titular: string;
    cnpj?: string;
    /** ISO. */
    validoDe: string;
    validoAte: string;
    enviadoEm?: string;
}

/** O que a tela edita. Texto como foi digitado: o servidor normaliza e recusa formato errado. */
export interface DadosDoEmitente {
    cnpj?: string;
    razaoSocial?: string;
    nomeFantasia?: string;
    inscricaoMunicipal?: string;
    regimeTributario?: RegimeTributario | '';
    email?: string;
    cep?: string;
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    municipio?: string;
    codigoMunicipio?: string;
    uf?: string;
    itemListaServico?: string;
    codigoTributacaoMunicipio?: string;
    cnae?: string;
    /** "2,5" ou "2.5". */
    aliquotaIss?: string;
    issRetido?: boolean;
    descricaoPadrao?: string;
    serieRps?: string;
    ambiente?: 'homologacao' | 'producao';
    emitirAoReceber?: boolean;
    /** Seis dígitos, como 060201 (padrão nacional). */
    codigoTributacaoNacional?: string;
    codigoNbs?: string;
    ibsCbsCst?: string;
    ibsCbsClassificacao?: string;
    /** Da tabela `REGIMES_ESPECIAIS`, como texto. */
    regimeEspecial?: string;
}

export interface EmitenteFiscal {
    cnpj: string | null;
    razaoSocial: string | null;
    nomeFantasia: string | null;
    inscricaoMunicipal: string | null;
    regimeTributario: RegimeTributario | null;
    email: string | null;
    cep: string | null;
    logradouro: string | null;
    numero: string | null;
    complemento: string | null;
    bairro: string | null;
    municipio: string | null;
    codigoMunicipio: string | null;
    uf: string | null;
    itemListaServico: string | null;
    codigoTributacaoMunicipio: string | null;
    cnae: string | null;
    aliquotaIss: number | null;
    issRetido: boolean;
    descricaoPadrao: string | null;
    serieRps: string;
    proximoNumeroRps: number;
    ambiente: 'homologacao' | 'producao';
    emitirAoReceber: boolean;
    codigoTributacaoNacional: string | null;
    codigoNbs: string | null;
    ibsCbsCst: string | null;
    ibsCbsClassificacao: string | null;
    regimeEspecial: number;
    /** O emissor intermediário. Nulo: nenhum ligado, e a emissão é recusada. */
    provedor: string | null;
    /** Nulo enquanto nunca se tentou ligar. */
    emissor: EmissorFiscal | null;
    certificado: CertificadoFiscal | null;
}

export type SituacaoDaNota = 'rascunho' | 'na_fila' | 'processando' | 'autorizada' | 'rejeitada' | 'cancelando' | 'cancelada';

export const SITUACAO_DA_NOTA: Record<SituacaoDaNota, { rotulo: string; tom: 'neutral' | 'warning' | 'success' | 'danger' | 'info' }> = {
    rascunho: { rotulo: 'Rascunho', tom: 'neutral' },
    na_fila: { rotulo: 'Na fila', tom: 'info' },
    processando: { rotulo: 'Na prefeitura', tom: 'info' },
    autorizada: { rotulo: 'Autorizada', tom: 'success' },
    rejeitada: { rotulo: 'Rejeitada', tom: 'danger' },
    cancelando: { rotulo: 'Cancelando', tom: 'warning' },
    cancelada: { rotulo: 'Cancelada', tom: 'neutral' },
};

export interface NotaFiscal {
    id: string;
    origem: AlvoFiscal;
    clinicaId: string;
    clinicaNome?: string;
    tituloId?: string;
    tituloDescricao?: string;
    faturaId?: string;
    faturaCompetencia?: string;
    situacao: SituacaoDaNota;
    tomadorNome?: string;
    tomadorDocumento?: string;
    tomadorEmail?: string;
    /** "Rua, número — bairro, município/UF, CEP", como a nota vai levar. */
    tomadorEndereco?: string;
    descricao: string;
    itemListaServico?: string;
    codigoTributacaoMunicipio?: string;
    codigoTributacaoNacional?: string;
    aliquotaIss?: number;
    issRetido: boolean;
    valorServicosCentavos: number;
    valorIssCentavos: number;
    /** `YYYY-MM-DD`, primeiro dia do mês. */
    competencia: string;
    rpsSerie?: string;
    rpsNumero?: number;
    numero?: string;
    codigoVerificacao?: string;
    autorizadaEm?: string;
    mensagem?: string;
    /** O PDF (DANFSe), o XML e o espelho na prefeitura, depois de autorizada. */
    pdfUrl?: string;
    xmlUrl?: string;
    espelhoUrl?: string;
    cancelamentoMotivo?: string;
    /** O que falta para emitir; vazio é "pronta". */
    pendencias: string[];
    criadoEm: string;
}

export interface FiltroDeNotas {
    origem: AlvoFiscal;
    tituloId?: string;
    faturaId?: string;
}

/** "12345678000195" → "12.345.678/0001-95"; "52998224725" → "529.982.247-25". */
export function documentoFormatado(doc: string | null | undefined): string {
    const d = (doc ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');
    if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
    if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
    return doc ?? '';
}

export type SituacaoDoCertificado =
    | { tipo: 'ausente' }
    | { tipo: 'vencido'; desde: string }
    | { tipo: 'vencendo'; dias: number; ate: string }
    | { tipo: 'valido'; ate: string };

/** Trinta dias antes de vencer já é aviso: renovar certificado leva dias. */
export function situacaoDoCertificado(certificado: CertificadoFiscal | null, agora: Date = new Date()): SituacaoDoCertificado {
    if (!certificado) return { tipo: 'ausente' };
    const fim = new Date(certificado.validoAte).getTime();
    if (fim <= agora.getTime()) return { tipo: 'vencido', desde: certificado.validoAte };
    const dias = Math.ceil((fim - agora.getTime()) / 86_400_000);
    return dias <= 30 ? { tipo: 'vencendo', dias, ate: certificado.validoAte } : { tipo: 'valido', ate: certificado.validoAte };
}

/** O arquivo como a borda recebe: base64, sem o prefixo `data:`. */
export async function arquivoEmBase64(arquivo: Blob): Promise<string> {
    const bytes = new Uint8Array(await arquivo.arrayBuffer());
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(bin);
}

/**
 * CPF ou CNPJ com dígito verificador certo — a mesma conta de
 * `app.documento_valido` (0150), inclusive o CNPJ alfanumérico de 2026.
 */
export function documentoValido(doc: string | null | undefined): boolean {
    const d = (doc ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');
    if (/^\d{11}$/.test(d)) {
        if (/^(\d)\1{10}$/.test(d)) return false;
        const dv = (n: number) => {
            let s = 0;
            for (let i = 0; i < n; i += 1) s += Number(d[i]) * (n + 1 - i);
            const r = (s * 10) % 11;
            return r === 10 ? 0 : r;
        };
        return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
    }
    if (/^[0-9A-Z]{12}\d{2}$/.test(d)) {
        if (/^(\d)\1{13}$/.test(d)) return false;
        const dv = (pesos: number[]) => {
            const s = pesos.reduce((acc, p, i) => acc + (d.charCodeAt(i) - 48) * p, 0);
            const r = s % 11;
            return r < 2 ? 0 : 11 - r;
        };
        return dv([5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(d[12])
            && dv([6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(d[13]);
    }
    return false;
}
